import * as React from "react";
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Stack,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ApartmentIcon from "@mui/icons-material/Apartment";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
// import LockIcon from "@mui/icons-material/Lock";
import PublicIcon from "@mui/icons-material/Public";
import VerifiedIcon from "@mui/icons-material/Verified";
import TokenitizeName from "../components/layout/TokenitizeName";

// ---- Theme setup (all landing-specific tokens live here) ----
const landingTheme = createTheme({
  palette: {
    mode: "dark",
    background: {
      default: "#0b1120",
      paper: "#0f172a",
    },
    primary: { main: "#0ea5e9", dark: "#0284c7" },
    secondary: { main: "#a855f7", dark: "#7e22ce" },
    success: { main: "#10b981" },
    text: {
      primary: "#ffffff",
      secondary: "#94a3b8",
      tertiary: "#cbd5e1",
    },
    /** Logo + brand mark colors */
    brand: {
      cyanLight: "#7dd3fc",
      cyan: "#38bdf8",
      slateLight: "#e5e7eb",
      purpleLight: "#f0abfc",
    },
    /** Gradient backgrounds for landing CTAs */
    landingGradients: {
      cyan: "linear-gradient(90deg,#2563eb,#22d3ee)",
      cyanHover: "linear-gradient(90deg,#1d4ed8,#06b6d4)",
      slate: "linear-gradient(90deg,#1e293b,#334155)",
      slateHover: "linear-gradient(90deg,#0f172a,#1e293b)",
      purple: "linear-gradient(90deg,#a855f7,#c026d3)",
      purpleHover: "linear-gradient(90deg,#9333ea,#a21caf)",
    },
    /** Surface tokens for card icons + outlines */
    landingSurface: {
      cardBg: "rgba(15,23,42,0.6)",
      cardBorder: "rgba(255,255,255,0.08)",
      iconBgCyan: "#06b6d4",
      iconBorderCyan: "rgba(255,255,255,0.12)",
      iconBgSlate: "#475569",
      iconBorderSlate: "rgba(255,255,255,0.12)",
      iconBgPurple: "#a855f7",
      iconBorderPurple: "rgba(255,255,255,0.12)",
      badgeBg: "rgba(255,255,255,0.05)",
      badgeBorder: "rgba(255,255,255,0.1)",
      headerBtnBg: "rgba(15,23,42,0.6)",
      headerBtnBorder: "rgba(148,163,184,0.25)",
      signInBg: "rgba(30,41,59,0.6)",
      signInBgHover: "rgba(30,41,59,0.85)",
      signInBorder: "rgba(148,163,184,0.18)",
    },
    /** Backdrop gradients (used in the page :before/:after) */
    landingBackdrop: {
      glowCyan: "rgba(56,189,248,0.15)",
      glowPurple: "rgba(168,85,247,0.15)",
      gridLine: "rgba(255,255,255,0.03)",
    },
  },
  shape: { borderRadius: 8 },
  typography: {
    fontFamily:
      'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    h2: { fontWeight: 800 },
    h6: { fontWeight: 600 },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { textTransform: "none", fontWeight: 600, borderRadius: 8 },
      },
    },
  },
});

function FeatureItem({ children }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <CheckCircleIcon sx={{ fontSize: 18, color: "success.main" }} />
      <Typography variant="body2" sx={{ color: "text.tertiary" }}>
        {children}
      </Typography>
    </Stack>
  );
}

function FooterBadge({ icon, label }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box sx={{ color: "brand.cyan", display: "flex", alignItems: "center" }}>
        {icon}
      </Box>
      <Typography variant="body2" sx={{ color: "text.tertiary", fontSize: 13 }}>
        {label}
      </Typography>
    </Stack>
  );
}

const cardSx = {
  p: 4,
  borderRadius: 2,
  width: "100%",
  maxWidth: "100%",
  boxSizing: "border-box",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  height: "100%",
  bgcolor: (theme) => theme.palette.landingSurface.cardBg,
  border: (theme) => `1px solid ${theme.palette.landingSurface.cardBorder}`,
  backdropFilter: "blur(12px)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
};

const iconBoxBase = {
  width: 52,
  height: 52,
  display: "grid",
  placeItems: "center",
  borderRadius: 1.5,
};

function TokenizeLanding() {
  const navigate = useNavigate();

  const handleGetStarted = (role) => {
    navigate(`/signup?role=${role}`);
  };

  const handleSignIn = (role) => {
    navigate(`/signin?role=${role}`);
  };

  return (
    <ThemeProvider theme={landingTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          overflow: "hidden",
          bgcolor: "background.default",
          "&:before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background: (theme) =>
              `radial-gradient(circle at 50% 20%, ${theme.palette.landingBackdrop.glowCyan}, transparent 60%),` +
              `radial-gradient(circle at 80% 80%, ${theme.palette.landingBackdrop.glowPurple}, transparent 70%)`,
            zIndex: 0,
          },
          "&:after": {
            content: '""',
            position: "absolute",
            inset: 0,
            backgroundImage: (theme) =>
              `linear-gradient(0deg, transparent 0, ${theme.palette.landingBackdrop.gridLine} 1px),` +
              `linear-gradient(90deg, transparent 0, ${theme.palette.landingBackdrop.gridLine} 1px)`,
            backgroundSize: "80px 80px",
            opacity: 0.2,
            zIndex: 0,
          },
        }}
      >
        {/* Header bar */}
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: { xs: 3, md: 6 },
            py: 3,
          }}
        >
          {/* Logo */}
          <Stack direction="row" spacing={1.5} alignItems="center">
            {/* <Box
              sx={{
                ...iconBoxBase,
                width: 38,
                height: 38,
                bgcolor: (theme) => theme.palette.landingSurface.iconBgCyan,
                border: (theme) =>
                  `1px solid ${theme.palette.landingSurface.iconBorderCyan}`,
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 18, color: "common.white" }} />
            </Box> */}
            <TokenitizeName height={36} />
          </Stack>
        </Box>

        {/* Hero + cards content */}
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            px: 2,
            pt: 4,
            pb: 6,
          }}
        >
          {/* Hero Text */}
          <Typography variant="h2" sx={{ color: "text.primary" }}>
            Tokenize Your World
          </Typography>
          <Typography sx={{ color: "text.secondary", mt: 2, fontSize: 18 }}>
            Invest in real-world assets through blockchain technology
          </Typography>
          <Typography
            sx={{
              color: "text.secondary",
              mt: 1,
              maxWidth: 800,
              mx: "auto",
              fontSize: 15,
            }}
          >
            Access tokenized real estate, commodities, and alternative
            investments with full transparency and security.
          </Typography>

          {/* Cards */}
          <Container maxWidth="lg" sx={{ mt: 8 }}>
            <Box
              sx={{
                display: "grid",
                width: "100%",
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  md: "repeat(3, minmax(0, 1fr))",
                },
                gap: { xs: 3, md: 4 },
                alignItems: "stretch",
              }}
            >
              {/* Investor */}
              <Paper sx={cardSx} elevation={0}>
                <Stack
                  spacing={2}
                  alignItems="flex-start"
                  sx={{ flex: 1, width: "100%" }}
                >
                  <Box
                    sx={{
                      ...iconBoxBase,
                      bgcolor: (theme) =>
                        theme.palette.landingSurface.iconBgCyan,
                      border: (theme) =>
                        `1px solid ${theme.palette.landingSurface.iconBorderCyan}`,
                    }}
                  >
                    <TrendingUpIcon sx={{ color: "common.white" }} />
                  </Box>

                  <Typography variant="h6" sx={{ color: "text.primary" }}>
                    I&apos;m an Investor
                  </Typography>

                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Discover and invest in tokenized assets
                  </Typography>

                  <Stack spacing={1.2} sx={{ mt: 1 }}>
                    <FeatureItem>Browse verified assets</FeatureItem>
                    <FeatureItem>Start with any amount</FeatureItem>
                    <FeatureItem>Earn passive income</FeatureItem>
                  </Stack>

                  <Stack
                    spacing={1.5}
                    sx={{ mt: "auto", pt: 2, width: "100%" }}
                  >
                    <Button
                      variant="contained"
                      endIcon={<ArrowForwardIcon />}
                      fullWidth
                      sx={{
                        py: 1.25,
                        color: "common.white",
                        background: (theme) =>
                          theme.palette.landingGradients.cyan,
                        ":hover": {
                          color: "common.white",
                          background: (theme) =>
                            theme.palette.landingGradients.cyanHover,
                        },
                      }}
                      onClick={() => handleGetStarted("investor")}
                    >
                      Get Started
                    </Button>
                    <Button
                      variant="outlined"
                      fullWidth
                      sx={{
                        py: 1.25,
                        bgcolor: (theme) =>
                          theme.palette.landingSurface.signInBg,
                        borderColor: (theme) =>
                          theme.palette.landingSurface.signInBorder,
                        color: "common.white",
                        "&:hover": {
                          borderColor: (theme) =>
                            theme.palette.landingSurface.signInBorder,
                          bgcolor: (theme) =>
                            theme.palette.landingSurface.signInBgHover,
                        },
                      }}
                      onClick={() => handleSignIn("investor")}
                    >
                      Sign In
                    </Button>
                  </Stack>
                </Stack>
              </Paper>

              {/* Asset Owner */}
              <Paper sx={cardSx} elevation={0}>
                <Stack
                  spacing={2}
                  alignItems="flex-start"
                  sx={{ flex: 1, width: "100%" }}
                >
                  <Box
                    sx={{
                      ...iconBoxBase,
                      bgcolor: (theme) =>
                        theme.palette.landingSurface.iconBgSlate,
                      border: (theme) =>
                        `1px solid ${theme.palette.landingSurface.iconBorderSlate}`,
                    }}
                  >
                    <ApartmentIcon sx={{ color: "common.white" }} />
                  </Box>
                  <Typography variant="h6" sx={{ color: "text.primary" }}>
                    I&apos;m an Asset Owner
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    List and tokenize your assets
                  </Typography>
                  <Stack spacing={1.2} sx={{ mt: 1 }}>
                    <FeatureItem>Unlock asset liquidity</FeatureItem>
                    <FeatureItem>Global investor access</FeatureItem>
                    <FeatureItem>Automated compliance</FeatureItem>
                  </Stack>
                  <Stack
                    spacing={1.5}
                    sx={{ mt: "auto", pt: 2, width: "100%" }}
                  >
                    <Button
                      variant="contained"
                      endIcon={<ArrowForwardIcon />}
                      fullWidth
                      sx={{
                        py: 1.25,
                        color: "common.white",
                        background: (theme) =>
                          theme.palette.landingGradients.slate,
                        ":hover": {
                          color: "common.white",
                          background: (theme) =>
                            theme.palette.landingGradients.slateHover,
                        },
                      }}
                      onClick={() => handleGetStarted("assetOwner")}
                    >
                      Get Started
                    </Button>
                    <Button
                      variant="outlined"
                      fullWidth
                      sx={{
                        py: 1.25,
                        bgcolor: (theme) =>
                          theme.palette.landingSurface.signInBg,
                        borderColor: (theme) =>
                          theme.palette.landingSurface.signInBorder,
                        color: "common.white",
                        "&:hover": {
                          borderColor: (theme) =>
                            theme.palette.landingSurface.signInBorder,
                          bgcolor: (theme) =>
                            theme.palette.landingSurface.signInBgHover,
                        },
                      }}
                      onClick={() => handleSignIn("assetOwner")}
                    >
                      Sign In
                    </Button>
                  </Stack>
                </Stack>
              </Paper>

              {/* Admin */}
              <Paper sx={cardSx} elevation={0}>
                <Stack
                  spacing={2}
                  alignItems="flex-start"
                  sx={{ flex: 1, width: "100%" }}
                >
                  <Box
                    sx={{
                      ...iconBoxBase,
                      bgcolor: (theme) =>
                        theme.palette.landingSurface.iconBgPurple,
                      border: (theme) =>
                        `1px solid ${theme.palette.landingSurface.iconBorderPurple}`,
                    }}
                  >
                    <ShieldOutlinedIcon sx={{ color: "common.white" }} />
                  </Box>
                  <Typography variant="h6" sx={{ color: "text.primary" }}>
                    I&apos;m an Admin
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Manage platform and oversee operations
                  </Typography>
                  <Stack spacing={1.2} sx={{ mt: 1 }}>
                    <FeatureItem>Manage all users</FeatureItem>
                    <FeatureItem>Review asset listings</FeatureItem>
                    <FeatureItem>Platform analytics</FeatureItem>
                  </Stack>
                  <Stack
                    spacing={1.5}
                    sx={{ mt: "auto", pt: 2, width: "100%" }}
                  >
                    <Button
                      variant="contained"
                      endIcon={<ShieldOutlinedIcon />}
                      fullWidth
                      sx={{
                        py: 1.25,
                        color: "common.white",
                        background: (theme) =>
                          theme.palette.landingGradients.purple,
                        ":hover": {
                          color: "common.white",
                          background: (theme) =>
                            theme.palette.landingGradients.purpleHover,
                        },
                      }}
                      onClick={() => handleSignIn("admin")}
                    >
                      Admin Access
                    </Button>
                    {/* Spacer to align footer height with the two-button cards */}
                    <Box
                      sx={{
                        height: { xs: 0, md: 42.5 },
                        flexShrink: 0,
                        visibility: { xs: "hidden", md: "visible" },
                        pointerEvents: "none",
                      }}
                      aria-hidden
                    />
                  </Stack>
                </Stack>
              </Paper>
            </Box>
          </Container>
        </Box>

        {/* Footer badges pinned to the bottom of the viewport */}
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            width: "100%",
            px: 2,
            py: 3,
          }}
        >
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            sx={{ flexWrap: "wrap", gap: 2 }}
          >
            {/* <FooterBadge
              icon={<LockIcon sx={{ fontSize: 16 }} />}
              label="Bank-Grade Security"
            /> */}
            <FooterBadge
              icon={<PublicIcon sx={{ fontSize: 16 }} />}
              label="Global Compliance"
            />
            <FooterBadge
              icon={<VerifiedIcon sx={{ fontSize: 16 }} />}
              label="Blockchain Verified"
            />
          </Stack>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default TokenizeLanding;
